/**
 * Human-friendly editor for `data-table` levels (design §15 Data mode) — the rows render
 * as a spreadsheet-style grid (typed cells, add/remove rows and fields, weight mode) so
 * property-driven games are editable without touching JSON. The JSON details block in
 * LevelDetail stays as the escape hatch; both edit the same undoable dataText.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { Plus, Copy, Trash2 } from 'lucide-react';
import type { DataTableLevel, DataTableRow } from '@volley/crucible-levels';
import { parseTagList } from './lib/tags';
import { Tip } from './Tip';

export type CellType = 'string' | 'number' | 'boolean';

export interface ColumnSpec {
  key: string;
  type: CellType;
}

/** Union of row fields as ordered columns: id, weight, refs, then first-seen order.
 *  A column's type comes from its first defined value. */
export function tableColumns(rows: DataTableRow[]): ColumnSpec[] {
  const order: string[] = [];
  const seen = new Set<string>();
  for (const key of ['id', 'weight', 'refs']) {
    if (rows.some((r) => r[key] !== undefined) || key === 'id') {
      order.push(key);
      seen.add(key);
    }
  }
  for (const r of rows) {
    for (const key of Object.keys(r)) {
      if (!seen.has(key)) {
        order.push(key);
        seen.add(key);
      }
    }
  }
  return order.map((key) => {
    const sample = rows.find((r) => r[key] !== undefined)?.[key];
    const type: CellType =
      typeof sample === 'number' ? 'number' : typeof sample === 'boolean' ? 'boolean' : 'string';
    return { key, type };
  });
}

/** Parse a cell edit back to its typed value. Empty string clears the field. */
export function coerceCell(raw: string, type: CellType): string | number | boolean | undefined {
  if (raw === '') return undefined;
  if (type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return raw;
}

/** A new row shaped like the last one (same fields, per-type defaults, unique id). */
export function blankRowLike(rows: DataTableRow[]): DataTableRow {
  const ids = new Set(rows.map((r) => r.id));
  let n = rows.length + 1;
  while (ids.has(`row-${n}`)) n += 1;
  const row: DataTableRow = { id: `row-${n}` };
  const template = rows[rows.length - 1];
  if (template) {
    for (const [key, value] of Object.entries(template)) {
      if (key === 'id' || value === undefined) continue;
      row[key] =
        typeof value === 'number'
          ? 0
          : typeof value === 'boolean'
            ? false
            : key === 'refs'
              ? []
              : '';
    }
  }
  return row;
}

function setRowField(
  rows: DataTableRow[],
  index: number,
  key: string,
  value: unknown,
): DataTableRow[] {
  return rows.map((r, i) => {
    if (i !== index) return r;
    const next = { ...r };
    if (value === undefined) delete next[key];
    else next[key] = value;
    return next;
  });
}

const CELL_INPUT =
  'w-full rounded border border-transparent bg-transparent px-1.5 py-0.5 text-xs text-crucible-fg outline-none hover:border-crucible-border focus:border-crucible-accent';

export function DataTableEditor({
  data,
  onChange,
}: {
  data: DataTableLevel;
  onChange: (next: DataTableLevel) => void;
}): ReactElement {
  const [newField, setNewField] = useState('');
  const [newFieldType, setNewFieldType] = useState<CellType>('string');
  const rows = data.rows;
  const columns = tableColumns(rows);

  const commitRows = (nextRows: DataTableRow[]): void => onChange({ ...data, rows: nextRows });

  const weightSum = rows.reduce((s, r) => s + (typeof r.weight === 'number' ? r.weight : 0), 0);

  const addField = (): void => {
    const key = newField.trim();
    if (!key || columns.some((c) => c.key === key)) return;
    const def = newFieldType === 'number' ? 0 : newFieldType === 'boolean' ? false : '';
    commitRows(rows.map((r) => ({ ...r, [key]: def })));
    setNewField('');
  };

  const removeField = (key: string): void => {
    commitRows(
      rows.map((r) => {
        const next = { ...r };
        delete next[key];
        return next;
      }),
    );
  };

  return (
    <div
      className="space-y-2 rounded-lg border border-crucible-border bg-crucible-bg p-3"
      data-testid="data-table-editor"
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-crucible-fg">Rows ({rows.length})</span>
        <label className="flex items-center gap-1.5 text-[11px] text-crucible-muted">
          Weights
          <select
            value={data.weightMode ?? ''}
            onChange={(e) =>
              onChange({
                ...data,
                weightMode: (e.target.value || undefined) as DataTableLevel['weightMode'],
              })
            }
            className="rounded border border-crucible-border bg-crucible-bg-elevated px-1.5 py-0.5 text-[11px] text-crucible-fg"
          >
            <option value="">unweighted</option>
            <option value="relative">relative (3 : 1 : …)</option>
            <option value="probability">probability (sum to 1)</option>
          </select>
        </label>
        {data.weightMode === 'probability' && (
          <span
            className={`text-[11px] ${Math.abs(weightSum - 1) < 1e-6 ? 'text-crucible-muted' : 'text-amber-300'}`}
          >
            sum {weightSum.toFixed(3)}
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="border-b border-crucible-border px-1.5 pb-1 text-left font-medium text-crucible-muted"
                >
                  <span className="inline-flex items-center gap-1">
                    {c.key}
                    <span className="text-[9px] uppercase opacity-60">{c.type}</span>
                    {c.key !== 'id' && (
                      <Tip label={`Remove the "${c.key}" field from every row`}>
                        <button
                          type="button"
                          aria-label={`Remove field ${c.key}`}
                          onClick={() => removeField(c.key)}
                          className="peer text-crucible-muted hover:text-red-400"
                        >
                          ×
                        </button>
                      </Tip>
                    )}
                  </span>
                </th>
              ))}
              <th className="border-b border-crucible-border" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="group">
                {columns.map((c) => (
                  <td key={c.key} className="border-b border-crucible-border/50 px-0.5 py-0.5">
                    {c.key === 'refs' ? (
                      <input
                        value={Array.isArray(row.refs) ? row.refs.join(', ') : ''}
                        placeholder="Content item names…"
                        onChange={(e) => {
                          const refs = parseTagList(e.target.value);
                          commitRows(
                            setRowField(rows, i, 'refs', refs.length > 0 ? refs : undefined),
                          );
                        }}
                        className={CELL_INPUT}
                      />
                    ) : c.type === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={row[c.key] === true}
                        onChange={(e) => commitRows(setRowField(rows, i, c.key, e.target.checked))}
                        className="ml-1.5"
                      />
                    ) : (
                      <input
                        type={c.type === 'number' ? 'number' : 'text'}
                        value={row[c.key] === undefined ? '' : String(row[c.key])}
                        onChange={(e) =>
                          commitRows(
                            setRowField(rows, i, c.key, coerceCell(e.target.value, c.type)),
                          )
                        }
                        className={`${CELL_INPUT} ${c.type === 'number' ? 'text-right' : ''}`}
                        data-testid={`data-cell-${i}-${c.key}`}
                      />
                    )}
                  </td>
                ))}
                <td className="border-b border-crucible-border/50 pl-1">
                  <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <Tip label="Duplicate row">
                      <button
                        type="button"
                        aria-label={`Duplicate row ${row.id}`}
                        onClick={() =>
                          commitRows([
                            ...rows.slice(0, i + 1),
                            { ...row, id: blankRowLike(rows).id },
                            ...rows.slice(i + 1),
                          ])
                        }
                        className="peer text-crucible-muted hover:text-crucible-fg"
                      >
                        <Copy size={11} />
                      </button>
                    </Tip>
                    <Tip label="Delete row">
                      <button
                        type="button"
                        aria-label={`Delete row ${row.id}`}
                        onClick={() => commitRows(rows.filter((_, ri) => ri !== i))}
                        className="peer text-crucible-muted hover:text-red-400"
                      >
                        <Trash2 size={11} />
                      </button>
                    </Tip>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => commitRows([...rows, blankRowLike(rows)])}
          data-testid="data-add-row"
          className="flex items-center gap-1 rounded border border-dashed border-crucible-border px-2 py-1 text-xs text-crucible-muted hover:text-crucible-fg"
        >
          <Plus size={12} /> Add row
        </button>
        <span className="flex items-center gap-1 text-xs text-crucible-muted">
          <input
            value={newField}
            onChange={(e) => setNewField(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addField();
            }}
            placeholder="new field name"
            data-testid="data-new-field"
            className="w-32 rounded border border-crucible-border bg-transparent px-2 py-1 text-xs text-crucible-fg outline-none focus:border-crucible-accent"
          />
          <select
            value={newFieldType}
            onChange={(e) => setNewFieldType(e.target.value as CellType)}
            className="rounded border border-crucible-border bg-crucible-bg-elevated px-1.5 py-1 text-xs text-crucible-fg"
          >
            <option value="string">text</option>
            <option value="number">number</option>
            <option value="boolean">yes/no</option>
          </select>
          <button
            type="button"
            onClick={addField}
            disabled={!newField.trim()}
            data-testid="data-add-field"
            className="flex items-center gap-1 rounded border border-dashed border-crucible-border px-2 py-1 text-xs text-crucible-muted hover:text-crucible-fg disabled:opacity-40"
          >
            <Plus size={12} /> Add field
          </button>
        </span>
      </div>
    </div>
  );
}
